import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaSnowflake, FaThermometerHalf, FaShieldAlt, FaCheckCircle, FaTools, FaChartLine, FaCog, FaFlask, FaWater } from 'react-icons/fa';

const CoolingTowerTreatment = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Cooling Tower Treatment</h1>
              <p className="lead mb-4">
                Advanced cooling water treatment solutions to prevent scale, corrosion, 
                and biological growth while maintaining optimal heat transfer efficiency.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Cooling Tower Treatment Importance</h2>
              <p className="lead">
                Effective cooling water treatment is essential for maintaining heat transfer efficiency, 
                preventing equipment damage, and ensuring safe operation of cooling systems.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Common Cooling System Problems:</h4>
              <ul>
                <li>Scale formation on heat exchange surfaces</li>
                <li>Corrosion of system components</li>
                <li>Microbial growth and biofouling</li>
                <li>Legionella and pathogen proliferation</li>
                <li>Reduced heat transfer efficiency</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Treatment Benefits:</h4>
              <ul>
                <li>Extended equipment lifespan</li>
                <li>Improved heat transfer efficiency</li>
                <li>Reduced maintenance costs</li>
                <li>Enhanced system reliability</li>
                <li>Compliance with health regulations</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Programs */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Comprehensive Treatment Programs</h2>
              <p className="lead">Multi-chemical approach for complete cooling system protection</p>
            </Col>
          </Row>
          
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaShieldAlt /></div>
                  <Card.Title>Scale Control</Card.Title>
                  <Card.Text>
                    Advanced antiscalants and scale inhibitors to prevent mineral deposits.
                  </Card.Text>
                  <Badge bg="primary">Essential</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaCog /></div>
                  <Card.Title>Corrosion Inhibition</Card.Title>
                  <Card.Text>
                    Multi-metal corrosion inhibitors for comprehensive equipment protection.
                  </Card.Text>
                  <Badge bg="danger">Critical</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaFlask /></div>
                  <Card.Title>Biocide Program</Card.Title>
                  <Card.Text>
                    Oxidizing and non-oxidizing biocides for microbial control and Legionella prevention.
                  </Card.Text>
                  <Badge bg="warning">Safety</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Chemicals */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Treatment Chemical Portfolio</h2>
              <p className="lead">Specialized chemicals for comprehensive cooling system protection</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaFlask /></div>
                <h5>Corrosion Inhibitors</h5>
                <p className="small">Phosphonates, molybdates, and azoles for multi-metal protection</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Scale Inhibitors</h5>
                <p className="small">Phosphate and polymer-based antiscalants for scale prevention</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaSnowflake /></div>
                <h5>Oxidizing Biocides</h5>
                <p className="small">Chlorine, bromine, and peroxide compounds for microbial control</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaWater /></div>
                <h5>Non-Oxidizing Biocides</h5>
                <p className="small">Quaternary ammonium and isothiazolin compounds for biofilm control</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Areas */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Cooling System Treatment Areas</h2>
              <p className="lead">Complete cooling circuit protection from make-up to blowdown</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Make-up Water Treatment</h4>
              <ul>
                <li>Water softening and filtration</li>
                <li>Chemical dosing and pH control</li>
                <li>Pre-treatment for scale prevention</li>
                <li>Quality monitoring and control</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaSnowflake className="me-2" />Cooling Water Treatment</h4>
              <ul>
                <li>Continuous scale and corrosion control</li>
                <li>Biocide dosing and microbial control</li>
                <li>pH and alkalinity management</li>
                <li>Blowdown optimization</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaThermometerHalf className="me-2" />Heat Exchange Surfaces</h4>
              <ul>
                <li>Scale prevention and removal</li>
                <li>Corrosion protection</li>
                <li>Biofilm control</li>
                <li>Heat transfer efficiency optimization</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaShieldAlt className="me-2" />Distribution System</h4>
              <ul>
                <li>Pipe and equipment protection</li>
                <li>Microbial growth prevention</li>
                <li>Flow optimization</li>
                <li>System cleanliness maintenance</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Monitoring & Control */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Monitoring & Control Systems</h2>
              <p className="lead">Advanced monitoring for optimal treatment performance</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Online Monitoring</h5>
                <p className="small">Real-time parameter tracking with automated alarms and controls</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Automated Dosing</h5>
                <p className="small">Precise chemical dosing with feedback control systems</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCheckCircle /></div>
                <h5>Microbial Testing</h5>
                <p className="small">Regular microbiological analysis and Legionella monitoring</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Performance Reporting</h5>
                <p className="small">Detailed performance reports and trend analysis</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* System Types */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Cooling System Types</h2>
              <p className="lead">Specialized treatment programs for different cooling configurations</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaSnowflake className="me-2" />Open Recirculating Systems</h4>
              <ul>
                <li>Most common cooling tower configuration</li>
                <li>High water concentration factor</li>
                <li>Requires comprehensive treatment program</li>
                <li>Regular blowdown management essential</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaThermometerHalf className="me-2" />Closed Loop Systems</h4>
              <ul>
                <li>Minimal water loss and makeup</li>
                <li>Lower chemical consumption</li>
                <li>Focus on corrosion inhibition</li>
                <li>Extended chemical life cycles</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Once-Through Systems</h4>
              <ul>
                <li>Single-pass water usage</li>
                <li>Simple treatment requirements</li>
                <li>Scale prevention primary focus</li>
                <li>Environmental discharge considerations</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Hybrid Systems</h4>
              <ul>
                <li>Combination of cooling technologies</li>
                <li>Customized treatment approaches</li>
                <li>Complex monitoring requirements</li>
                <li>Integrated chemical management</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Technical Specifications</h2>
              <p className="lead">Treatment parameters and performance standards</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <h4 className="mb-3">Water Quality Parameters:</h4>
              <ul>
                <li>pH: 7.0-9.0 (system dependent)</li>
                <li>Conductivity: As per system design</li>
                <li>Alkalinity: 100-500 ppm as CaCO3</li>
                <li>Hardness: &lt;500 ppm as CaCO3</li>
                <li>Iron: &lt;1.0 ppm</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Treatment Equipment:</h4>
              <ul>
                <li>Chemical dosing pumps and tanks</li>
                <li>Automatic blowdown controllers</li>
                <li>Online monitoring instrumentation</li>
                <li>Water softening and filtration systems</li>
                <li>Sidestream filtration equipment</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need Cooling Tower Treatment Solutions?</h2>
              <p className="lead mb-4">
                Contact us for a comprehensive cooling system assessment and customized treatment program.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  System Assessment
                </Button>
                <Button variant="outline-light" size="lg">
                  Legionella Testing
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default CoolingTowerTreatment;
